const features = [
  {
    icon: "ri-speed-line",
    title: "Blazing Performance",
    desc: "Optimized native Swift code that runs at full hardware speed — no slowdowns, no battery drain.",
    tag: "Performance",
    dark: true,
  },
  {
    icon: "ri-shield-user-line",
    title: "Privacy by Design",
    desc: "Zero data collection policy. Your activity, files, and location are never transmitted or stored.",
    tag: "Privacy",
    dark: false,
    image:
      "https://public.readdy.ai/ai/img_res/32a227c581269272a6e2204c3d1c494c.jpg",
  },
  {
    icon: "ri-refresh-line",
    title: "Real-Time Updates",
    desc: "Threat databases and cleaning algorithms update silently in the background, always current.",
    tag: "Updates",
    dark: false,
  },
  {
    icon: "ri-notification-badge-line",
    title: "Smart Notifications",
    desc: "Context-aware alerts only when action is needed — no spam, no noise.",
    tag: "UX",
    dark: true,
  },
  {
    icon: "ri-fingerprint-line",
    title: "Biometric Auth",
    desc: "Face ID and Touch ID integration for instant, frictionless secure access.",
    tag: "Security",
    dark: false,
  },
  {
    icon: "ri-settings-4-line",
    title: "One-Tap Actions",
    desc: "Complex tasks simplified — clean, secure, or protect your device in a single tap.",
    tag: "Simplicity",
    dark: true,
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="py-24 md:py-32" style={{ background: "#faf9f7" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">
              Core Features
            </span>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              What makes<br />
              <span className="italic font-light text-gray-500">our apps stand out</span>
            </h2>
          </div>
          <p className="text-gray-500 text-sm md:text-base max-w-xs leading-relaxed">
            Every feature is intentional, refined through thousands of hours of user research and
            real-world iOS testing.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feat) => (
            <div
              key={feat.title}
              className={`rounded-3xl p-7 flex flex-col gap-4 relative overflow-hidden min-h-[200px] ${
                feat.dark ? "bg-gray-900 text-white" : "bg-white text-gray-900"
              }`}
            >
              {feat.image && (
                <div className="absolute inset-0 opacity-20">
                  <img src={feat.image} alt="" className="w-full h-full object-cover object-top" />
                </div>
              )}
              <div className="relative z-10 flex items-start justify-between">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                    feat.dark ? "bg-white/10" : "bg-gray-100"
                  }`}
                >
                  <i
                    className={`${feat.icon} text-lg ${feat.dark ? "text-white" : "text-gray-700"}`}
                  />
                </div>
                <span
                  className={`text-xs font-medium px-3 py-1 rounded-full ${
                    feat.dark ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {feat.tag}
                </span>
              </div>
              <div className="relative z-10 mt-auto">
                <h3 className={`font-bold text-base mb-1.5 ${feat.dark ? "text-white" : "text-gray-900"}`}>
                  {feat.title}
                </h3>
                <p className={`text-sm leading-relaxed ${feat.dark ? "text-white/60" : "text-gray-500"}`}>
                  {feat.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom highlight */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { num: "256-bit", label: "AES Encryption Standard", icon: "ri-lock-2-line" },
            { num: "99.9%", label: "Uptime Reliability", icon: "ri-server-line" },
            { num: "&lt;50ms", label: "Average Response Time", icon: "ri-timer-flash-line" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-gray-100"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-xl flex-shrink-0">
                <i className={`${item.icon} text-white text-lg`} />
              </div>
              <div>
                <p
                  className="text-xl font-bold text-gray-900"
                  dangerouslySetInnerHTML={{ __html: item.num }}
                />
                <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
